import React, { Component, ErrorInfo, ReactNode, useMemo, useState } from 'react'
import { useEditor, EditorContent, Editor } from '@tiptap/react'
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  TextField,
  Button,
  Menu,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatClearIcon from '@mui/icons-material/FormatClear'
import LinkIcon from '@mui/icons-material/Link'
import TableChartIcon from '@mui/icons-material/TableChart'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import ViewListIcon from '@mui/icons-material/ViewList'
import SearchIcon from '@mui/icons-material/Search'
import {
  getEditorExtensions,
  stripPastedHTML,
  cleanPastedArticleHTML,
  normalizeHref,
  RichTextVariant,
} from './extensions'

// Error Boundary to catch TipTap errors
interface ErrorBoundaryState {
  hasError: boolean
}

class EditorErrorBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('RichTextEditor error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
  /** 'article' adds links, tables, fact boxes (blockquote) and embeds. 'default' is the simple editor. */
  variant?: RichTextVariant
}

// Block format options for dropdown
const BLOCK_FORMATS = [
  { value: 'paragraph', label: 'Normal tekst' },
  { value: 'heading2', label: 'Overskrift 2' },
  { value: 'heading3', label: 'Overskrift 3' },
  { value: 'heading4', label: 'Overskrift 4' },
]

// Fallback simple textarea if TipTap fails
function SimpleFallbackEditor({ value, onChange, placeholder, minHeight, variant }: RichTextEditorProps) {
  if (variant === 'article') {
    // Articles contain tables and embeds, so edit the raw HTML instead of stripping it
    return (
      <TextField
        multiline
        fullWidth
        minRows={Math.floor((minHeight || 200) / 24)}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        helperText="Simpel editor (rich text ikke tilgængelig). Du redigerer HTML direkte."
        sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
      />
    )
  }

  return (
    <TextField
      multiline
      fullWidth
      minRows={Math.floor(minHeight! / 24)}
      value={value?.replace(/<[^>]*>/g, '') || ''}
      onChange={(e) => onChange(`<p>${e.target.value}</p>`)}
      placeholder={placeholder}
      helperText="Simpel editor (rich text ikke tilgængelig)"
    />
  )
}

// Accept /sti/, #anker, https://, mailto: and tel:
const VALID_HREF = /^(\/|#|https?:\/\/|mailto:|tel:)/i

function LinkDialog({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const existingHref: string = editor.getAttributes('link').href || ''
  // With no selected text we insert the link text as well
  const needsText = editor.state.selection.empty && !editor.isActive('link')
  const [href, setHref] = useState(existingHref)
  const [text, setText] = useState('')

  const trimmed = href.trim()
  // "borger.dk/sti" without protocol becomes https://borger.dk/sti
  const withProtocol = /^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#]|$)/i.test(trimmed) ? `https://${trimmed}` : trimmed
  const finalHref = withProtocol ? normalizeHref(withProtocol) : ''
  const valid = !finalHref || VALID_HREF.test(finalHref)

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    onClose()
  }

  const applyLink = () => {
    if (!finalHref) {
      removeLink()
      return
    }
    if (needsText) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'text',
          text: text.trim() || finalHref,
          marks: [{ type: 'link', attrs: { href: finalHref } }],
        })
        .run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: finalHref }).run()
    }
    onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle>{existingHref ? 'Rediger link' : 'Indsæt link'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Adresse"
            placeholder="/begravelse/"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid && finalHref) {
                e.preventDefault()
                applyLink()
              }
            }}
            error={!valid}
            helperText={
              valid
                ? finalHref && finalHref !== trimmed
                  ? `Gemmes som ${finalHref}`
                  : 'Interne links skrives som sti, f.eks. /begravelse/. Eksterne links med https://'
                : 'Start med / for interne links eller https:// for eksterne'
            }
          />
          {needsText && (
            <TextField
              fullWidth
              label="Linktekst"
              value={text}
              onChange={(e) => setText(e.target.value)}
              helperText="Tom = adressen vises som tekst"
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {existingHref && (
          <Button color="error" onClick={removeLink} sx={{ mr: 'auto' }}>
            Fjern link
          </Button>
        )}
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" onClick={applyLink} disabled={!valid || !finalHref}>
          Gem
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const toolbarTextButtonSx = {
  textTransform: 'none',
  fontSize: 13,
  height: 28,
  px: 1,
  color: 'text.primary',
} as const

// Editor-only styling for article elements (the website has its own styles)
const articleContentSx = {
  '& a': {
    color: 'primary.main',
    textDecoration: 'underline',
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'primary.main',
    bgcolor: 'grey.50',
    borderRadius: 1,
    mx: 0,
    my: 2,
    px: 2,
    py: 1,
    '& p:last-child': { mb: 0 },
  },
  '& table': {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: '100%',
    my: 2,
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 1,
    py: 0.75,
    verticalAlign: 'top',
    position: 'relative',
    '& p': { mb: 0 },
  },
  '& th': {
    bgcolor: 'grey.100',
    fontWeight: 600,
    textAlign: 'left',
  },
  '& .selectedCell::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    bgcolor: 'rgba(92, 75, 153, 0.12)',
    pointerEvents: 'none',
  },
} as const

function TipTapEditor({ value, onChange, placeholder, minHeight = 200, variant = 'default' }: RichTextEditorProps) {
  const isArticle = variant === 'article'
  const extensions = useMemo(() => getEditorExtensions(variant, placeholder), [variant, placeholder])
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [tableMenuAnchor, setTableMenuAnchor] = useState<HTMLElement | null>(null)

  const editor = useEditor({
    // immediatelyRender: false prevents duplicate initialization in React StrictMode
    immediatelyRender: false,
    extensions,
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
    // Handle paste to strip unwanted content
    editorProps: {
      // Default: remove links, images and iframes. Article: keep links, remove images and iframes.
      transformPastedHTML: isArticle ? cleanPastedArticleHTML : stripPastedHTML,
      handleDrop: (view, event, slice, moved) => {
        // Prevent dropping files/images
        if (event.dataTransfer?.files?.length) {
          event.preventDefault()
          return true
        }
        return false
      },
    },
  })

  // Update content when value prop changes (from parent)
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [value, editor])

  if (!editor) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight }}>
        <CircularProgress size={24} />
      </Paper>
    )
  }

  // Get current block format for dropdown
  const getCurrentBlockFormat = () => {
    if (editor.isActive('heading', { level: 2 })) return 'heading2'
    if (editor.isActive('heading', { level: 3 })) return 'heading3'
    if (editor.isActive('heading', { level: 4 })) return 'heading4'
    return 'paragraph'
  }

  // Set block format from dropdown
  const setBlockFormat = (format: string) => {
    switch (format) {
      case 'heading2':
        editor.chain().focus().toggleHeading({ level: 2 }).run()
        break
      case 'heading3':
        editor.chain().focus().toggleHeading({ level: 3 }).run()
        break
      case 'heading4':
        editor.chain().focus().toggleHeading({ level: 4 }).run()
        break
      default:
        editor.chain().focus().setParagraph().run()
    }
  }

  // Table menu (article variant only, the default editor has no table commands)
  const inTable = isArticle && editor.isActive('table')
  const tableActions = !isArticle || !tableMenuAnchor ? [] : [
    {
      label: 'Indsæt tabel (3 x 3 med overskriftsrække)',
      disabled: inTable,
      run: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    },
    { label: 'Tilføj række', disabled: !editor.can().addRowAfter(), run: () => editor.chain().focus().addRowAfter().run() },
    { label: 'Tilføj kolonne', disabled: !editor.can().addColumnAfter(), run: () => editor.chain().focus().addColumnAfter().run() },
    { label: 'Slet række', disabled: !editor.can().deleteRow(), run: () => editor.chain().focus().deleteRow().run() },
    { label: 'Slet kolonne', disabled: !editor.can().deleteColumn(), run: () => editor.chain().focus().deleteColumn().run() },
    { label: 'Slet tabel', disabled: !editor.can().deleteTable(), run: () => editor.chain().focus().deleteTable().run() },
  ]

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid', 
        borderColor: editor.isFocused ? 'primary.main' : 'divider', 
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.5,
          p: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Tooltip title="Halvfed (Ctrl+B)">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().toggleBold().run()}
            sx={{ 
              bgcolor: editor.isActive('bold') ? 'action.selected' : 'transparent',
            }}
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Kursiv (Ctrl+I)">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().toggleItalic().run()}
            sx={{ 
              bgcolor: editor.isActive('italic') ? 'action.selected' : 'transparent',
            }}
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Understreget (Ctrl+U)">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            sx={{ 
              bgcolor: editor.isActive('underline') ? 'action.selected' : 'transparent',
            }}
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Block format dropdown */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={getCurrentBlockFormat()}
            onChange={(e) => setBlockFormat(e.target.value)}
            sx={{ 
              fontSize: 13, 
              height: 28,
              '& .MuiSelect-select': { py: 0.5, px: 1 }
            }}
          >
            {BLOCK_FORMATS.map((format) => (
              <MenuItem key={format.value} value={format.value} sx={{ fontSize: 13 }}>
                {format.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Fjern formatering">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().unsetAllMarks().setParagraph().run()}
          >
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Tooltip title="Punktliste">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            sx={{ 
              bgcolor: editor.isActive('bulletList') ? 'action.selected' : 'transparent',
            }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Nummereret liste">
          <IconButton 
            size="small" 
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            sx={{ 
              bgcolor: editor.isActive('orderedList') ? 'action.selected' : 'transparent',
            }}
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {isArticle && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Tooltip title="Link (indsæt, rediger eller fjern)">
              <IconButton
                size="small"
                onClick={() => setLinkDialogOpen(true)}
                sx={{
                  bgcolor: editor.isActive('link') ? 'action.selected' : 'transparent',
                }}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Tabel">
              <IconButton
                size="small"
                onClick={(e) => setTableMenuAnchor(e.currentTarget)}
                sx={{
                  bgcolor: inTable ? 'action.selected' : 'transparent',
                }}
              >
                <TableChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Button
              size="small"
              startIcon={<FormatQuoteIcon fontSize="small" />}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              sx={{
                ...toolbarTextButtonSx,
                bgcolor: editor.isActive('blockquote') ? 'action.selected' : 'transparent',
              }}
            >
              Faktaboks
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Button
              size="small"
              startIcon={<ViewListIcon fontSize="small" />}
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertEmbed({ 'data-embed': 'listing', 'data-orderby': 'rating', 'data-limit': '4' })
                  .run()
              }
              sx={toolbarTextButtonSx}
            >
              Indsæt bedemandsliste
            </Button>

            <Button
              size="small"
              startIcon={<SearchIcon fontSize="small" />}
              onClick={() => editor.chain().focus().insertEmbed({ 'data-embed': 'search' }).run()}
              sx={toolbarTextButtonSx}
            >
              Indsæt adressesøgning
            </Button>

            <Menu
              anchorEl={tableMenuAnchor}
              open={Boolean(tableMenuAnchor)}
              onClose={() => setTableMenuAnchor(null)}
              disableRestoreFocus
            >
              {tableActions.map((action) => (
                <MenuItem
                  key={action.label}
                  disabled={action.disabled}
                  onClick={() => {
                    setTableMenuAnchor(null)
                    action.run()
                  }}
                >
                  <ListItemText primaryTypographyProps={{ fontSize: 14 }}>{action.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>

            {linkDialogOpen && <LinkDialog editor={editor} onClose={() => setLinkDialogOpen(false)} />}
          </>
        )}
      </Box>

      {/* Editor Content */}
      <Box
        sx={{
          '& .tiptap': {
            p: 2,
            minHeight,
            outline: 'none',
            fontFamily: 'inherit',
            fontSize: '1rem',
            lineHeight: 1.6,
            
            // Placeholder styling
            '& p.is-editor-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              color: 'text.secondary',
              opacity: 0.6,
              pointerEvents: 'none',
              float: 'left',
              height: 0,
            },
            
            // Heading styles
            '& h2': {
              fontSize: '1.75rem',
              fontWeight: 600,
              mt: 2,
              mb: 1,
              lineHeight: 1.3,
            },
            '& h3': {
              fontSize: '1.5rem',
              fontWeight: 600,
              mt: 1.5,
              mb: 1,
              lineHeight: 1.3,
            },
            '& h4': {
              fontSize: '1.25rem',
              fontWeight: 600,
              mt: 1.5,
              mb: 0.75,
              lineHeight: 1.4,
            },
            
            // Paragraph
            '& p': {
              mb: 1,
            },
            
            // Lists
            '& ul, & ol': {
              pl: 3,
              mb: 1,
            },
            '& li': {
              mb: 0.5,
            },
            
            // Inline styles
            '& strong': {
              fontWeight: 700,
            },
            '& em': {
              fontStyle: 'italic',
            },
            '& u': {
              textDecoration: 'underline',
            },

            // Links, fact boxes and tables (article variant only)
            ...(isArticle ? articleContentSx : {}),
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Paper>
  )
}

// Main export with error boundary
export default function RichTextEditor(props: RichTextEditorProps) {
  return (
    <EditorErrorBoundary fallback={<SimpleFallbackEditor {...props} />}>
      <TipTapEditor {...props} />
    </EditorErrorBoundary>
  )
}
